import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Code, CheckCircle, ArrowRight, Database, Zap, Shield,
  FileText, Terminal, Globe, Lock, Clock, BarChart3,
  Key, Book, Webhook, Server, Phone, Mail, Copy, Check
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ApiPartners = () => {
  const navigate = useNavigate();
  const [copiedEndpoint, setCopiedEndpoint] = useState(null);

  const apiFeatures = [
    {
      icon: Database,
      title: 'Patient Data Management',
      description: 'CRUD operations for patient records, medical history, and health profiles with HIPAA compliance',
      color: '#1D57D5'
    },
    {
      icon: Calendar,
      title: 'Appointment APIs',
      description: 'Book, manage, and sync appointments across your platform with real-time availability',
      color: '#28C08A'
    },
    {
      icon: FileText,
      title: 'EHR Integration',
      description: 'Seamless electronic health records integration with HL7 and FHIR standards support',
      color: '#F97316'
    },
    {
      icon: Webhook,
      title: 'Real-Time Webhooks',
      description: 'Instant notifications for appointments, consultations, prescriptions, and patient events',
      color: '#9333EA'
    },
    {
      icon: Lock,
      title: 'OAuth 2.0 Authentication',
      description: 'Secure authentication with token-based access control and refresh mechanisms',
      color: '#E91E8C'
    },
    {
      icon: BarChart3,
      title: 'Analytics & Reporting',
      description: 'Access healthcare analytics, utilization metrics, and custom reporting endpoints',
      color: '#F4CE7B'
    }
  ];

  const endpoints = [
    {
      method: 'POST',
      endpoint: '/api/v1/patients',
      description: 'Create new patient record',
      color: '#28C08A'
    },
    {
      method: 'GET',
      endpoint: '/api/v1/appointments',
      description: 'Retrieve appointment list',
      color: '#1D57D5'
    },
    {
      method: 'POST',
      endpoint: '/api/v1/consultations',
      description: 'Initiate video consultation',
      color: '#F97316'
    },
    {
      method: 'GET',
      endpoint: '/api/v1/prescriptions/:id',
      description: 'Fetch prescription details',
      color: '#9333EA'
    }
  ];

  const benefits = [
    'RESTful API with JSON responses',
    'Comprehensive developer documentation',
    'Sandbox environment for testing',
    'Rate limiting: 10,000 requests/hour',
    'Uptime SLA of 99.9%',
    '24/7 technical support',
    'HIPAA & GDPR compliant',
    'Free tier for development'
  ];

  const useCases = [
    {
      title: 'Health Insurance Platforms',
      description: 'Integrate teleconsultation claims processing, provider networks, and patient eligibility verification',
      icon: Shield,
      gradient: 'linear-gradient(135deg, #1D57D5 0%, #286CFF 100%)'
    },
    {
      title: 'Pharmacy Management Systems',
      description: 'Connect prescription fulfillment, inventory management, and patient medication history',
      icon: FileText,
      gradient: 'linear-gradient(135deg, #28C08A 0%, #20A577 100%)'
    },
    {
      title: 'Corporate Wellness Apps',
      description: 'Embed healthcare services, health assessments, and provider consultations into your employee benefits platform',
      icon: TrendingUp,
      gradient: 'linear-gradient(135deg, #F4CE7B 0%, #E8C068 100%)'
    },
    {
      title: 'Medical Device Companies',
      description: 'Stream vitals data, integrate remote monitoring, and trigger provider alerts based on patient metrics',
      icon: Activity,
      gradient: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)'
    }
  ];

  const handleCopyEndpoint = (endpoint, index) => {
    navigator.clipboard.writeText(endpoint);
    setCopiedEndpoint(index);
    setTimeout(() => setCopiedEndpoint(null), 2000);
  };

  return (
    <div style={{ background: 'white', minHeight: '100vh' }}>
      <Navbar />
      
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #0D2C6E 0%, #1D57D5 50%, #9333EA 100%)',
        paddingTop: '120px',
        paddingBottom: '100px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(244, 206, 123, 0.2) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)'
        }} />

        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 32px', position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(244, 206, 123, 0.15)',
            backdropFilter: 'blur(10px)',
            border: '2px solid rgba(244, 206, 123, 0.3)',
            borderRadius: '50px',
            padding: '10px 24px',
            marginBottom: '32px'
          }}>
            <Code size={20} color="#F4CE7B" />
            <span style={{ color: '#F4CE7B', fontSize: '14px', fontWeight: '700', letterSpacing: '0.5px' }}>
              FOR API INTEGRATION PARTNERS
            </span>
          </div>

          <h1 style={{
            fontSize: '64px',
            fontWeight: '900',
            color: 'white',
            lineHeight: '1.1',
            marginBottom: '24px',
            letterSpacing: '-2px'
          }}>
            Build on GEWO's<br />
            <span style={{
              background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Healthcare API Platform
            </span>
          </h1>

          <p style={{
            fontSize: '20px',
            color: 'rgba(255, 255, 255, 0.9)',
            lineHeight: '1.6',
            maxWidth: '650px',
            marginBottom: '40px'
          }}>
            Access India's largest verified healthcare network through our comprehensive RESTful APIs. HIPAA-compliant, developer-friendly, and production-ready.
          </p>

          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <button
              onClick={() => window.open('https://docs.gewohealth.com/api', '_blank')}
              style={{
                background: 'linear-gradient(135deg, #F4CE7B 0%, #E8C068 100%)',
                color: '#0D2C6E',
                border: 'none',
                borderRadius: '50px',
                padding: '18px 40px',
                fontSize: '18px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                boxShadow: '0 8px 24px rgba(244, 206, 123, 0.4)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(244, 206, 123, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(244, 206, 123, 0.4)';
              }}
            >
              View API Documentation
              <Book size={20} />
            </button>
            <button
              onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '50px',
                padding: '18px 40px',
                fontSize: '18px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.border = '2px solid rgba(255, 255, 255, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.border = '2px solid rgba(255, 255, 255, 0.3)';
              }}
            >
              Get API Key
            </button>
          </div>

          {/* Trust Indicators */}
          <div style={{
            display: 'flex',
            gap: '48px',
            marginTop: '48px',
            flexWrap: 'wrap'
          }}>
            <div>
              <div style={{ fontSize: '36px', fontWeight: '900', color: '#F4CE7B' }}>99.9%</div>
              <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)' }}>API Uptime</div>
            </div>
            <div>
              <div style={{ fontSize: '36px', fontWeight: '900', color: '#F4CE7B' }}>50M+</div>
              <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)' }}>API Calls/Month</div>
            </div>
            <div>
              <div style={{ fontSize: '36px', fontWeight: '900', color: '#F4CE7B' }}>200+</div>
              <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)' }}>Integration Partners</div>
            </div>
          </div>
        </div>
      </div>

      {/* API Features */}
      <div style={{
        background: 'linear-gradient(180deg, #F8FAFC 0%, white 100%)',
        padding: '100px 0'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 32px' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 style={{
              fontSize: '48px',
              fontWeight: '900',
              color: '#1A202C',
              marginBottom: '16px',
              letterSpacing: '-1px'
            }}>
              Comprehensive Healthcare APIs
            </h2>
            <p style={{
              fontSize: '20px',
              color: '#64748B',
              maxWidth: '700px',
              margin: '0 auto'
            }}>
              Everything you need to integrate healthcare services into your platform
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '32px'
          }}>
            {apiFeatures.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div
                  key={index}
                  style={{
                    background: 'white',
                    border: '2px solid #E8ECF0',
                    borderRadius: '20px',
                    padding: '40px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.12)';
                    e.currentTarget.style.borderColor = feature.color;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#E8ECF0';
                  }}
                >
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '72px',
                    height: '72px',
                    background: `${feature.color}15`,
                    borderRadius: '16px',
                    marginBottom: '24px'
                  }}>
                    <IconComponent size={36} color={feature.color} />
                  </div>
                  <h3 style={{
                    fontSize: '22px',
                    fontWeight: '700',
                    color: '#1A202C',
                    marginBottom: '12px'
                  }}>
                    {feature.title}
                  </h3>
                  <p style={{
                    fontSize: '16px',
                    color: '#64748B',
                    lineHeight: '1.7'
                  }}>
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* API Endpoints Preview */}
      <div style={{ background: 'white', padding: '100px 0' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 32px' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{
              fontSize: '48px',
              fontWeight: '900',
              color: '#1A202C',
              marginBottom: '16px',
              letterSpacing: '-1px'
            }}>
              Sample API Endpoints
            </h2>
            <p style={{
              fontSize: '18px',
              color: '#64748B'
            }}>
              Developer-friendly RESTful endpoints with comprehensive documentation
            </p>
          </div>

          <div style={{
            background: '#1A202C',
            borderRadius: '20px',
            padding: '32px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }}>
            {endpoints.map((endpoint, index) => (
              <div
                key={index}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: index < endpoints.length - 1 ? '16px' : '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <span style={{
                      display: 'inline-block',
                      background: endpoint.color,
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: '700',
                      padding: '4px 12px',
                      borderRadius: '6px'
                    }}>
                      {endpoint.method}
                    </span>
                    <code style={{
                      fontSize: '16px',
                      fontFamily: 'monospace',
                      color: '#F4CE7B',
                      fontWeight: '600'
                    }}>
                      {endpoint.endpoint}
                    </code>
                  </div>
                  <p style={{
                    fontSize: '14px',
                    color: 'rgba(255, 255, 255, 0.7)',
                    marginLeft: '78px'
                  }}>
                    {endpoint.description}
                  </p>
                </div>
                <button
                  onClick={() => handleCopyEndpoint(endpoint.endpoint, index)}
                  style={{
                    background: 'rgba(244, 206, 123, 0.1)',
                    border: '1px solid rgba(244, 206, 123, 0.3)',
                    borderRadius: '8px',
                    padding: '10px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(244, 206, 123, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(244, 206, 123, 0.1)';
                  }}
                >
                  {copiedEndpoint === index ? (
                    <Check size={18} color="#28C08A" />
                  ) : (
                    <Copy size={18} color="#F4CE7B" />
                  )}
                </button>
              </div>
            ))}
          </div>

          <div style={{
            textAlign: 'center',
            marginTop: '32px'
          }}>
            <button
              onClick={() => window.open('https://docs.gewohealth.com/api', '_blank')}
              style={{
                background: 'linear-gradient(135deg, #1D57D5 0%, #286CFF 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '50px',
                padding: '16px 32px',
                fontSize: '16px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(29, 87, 213, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              View Full API Reference
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Use Cases */}
      <div style={{
        background: 'linear-gradient(180deg, #F8FAFC 0%, white 100%)',
        padding: '100px 0'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 32px' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 style={{
              fontSize: '48px',
              fontWeight: '900',
              color: '#1A202C',
              marginBottom: '16px',
              letterSpacing: '-1px'
            }}>
              API Integration Use Cases
            </h2>
            <p style={{
              fontSize: '20px',
              color: '#64748B',
              maxWidth: '700px',
              margin: '0 auto'
            }}>
              See how leading companies are integrating GEWO APIs
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {useCases.map((useCase, index) => {
              const IconComponent = useCase.icon;
              return (
                <div
                  key={index}
                  style={{
                    background: 'white',
                    border: '2px solid #E8ECF0',
                    borderRadius: '20px',
                    padding: '36px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.12)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '64px',
                    height: '64px',
                    background: useCase.gradient,
                    borderRadius: '16px',
                    marginBottom: '20px'
                  }}>
                    <IconComponent size={32} color="white" />
                  </div>
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#1A202C',
                    marginBottom: '12px'
                  }}>
                    {useCase.title}
                  </h3>
                  <p style={{
                    fontSize: '15px',
                    color: '#64748B',
                    lineHeight: '1.6'
                  }}>
                    {useCase.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div style={{ background: 'white', padding: '100px 0' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 32px' }}>
          <h2 style={{
            fontSize: '48px',
            fontWeight: '900',
            color: '#1A202C',
            marginBottom: '48px',
            textAlign: 'center',
            letterSpacing: '-1px'
          }}>
            What You Get
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '24px'
          }}>
            {benefits.map((benefit, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  minWidth: '28px',
                  minHeight: '28px',
                  background: 'linear-gradient(135deg, #1D57D5 0%, #286CFF 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CheckCircle size={16} color="white" />
                </div>
                <span style={{ fontSize: '16px', color: '#4A4A4A', fontWeight: '500' }}>
                  {benefit}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact CTA */}
      <div id="contact-form" style={{
        background: 'linear-gradient(135deg, #0D2C6E 0%, #1D57D5 50%, #9333EA 100%)',
        padding: '100px 0'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: '0 32px',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontSize: '56px',
            fontWeight: '900',
            color: 'white',
            lineHeight: '1.2',
            marginBottom: '24px',
            letterSpacing: '-1px'
          }}>
            Start Building Today
          </h2>
          <p style={{
            fontSize: '20px',
            color: 'rgba(255, 255, 255, 0.9)',
            marginBottom: '48px',
            lineHeight: '1.6'
          }}>
            Get your API credentials and start integrating healthcare services into your platform.
          </p>

          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => window.open('mailto:developers@gewohealth.com', '_blank')}
              style={{
                background: 'linear-gradient(135deg, #F4CE7B 0%, #E8C068 100%)',
                color: '#0D2C6E',
                border: 'none',
                borderRadius: '50px',
                padding: '20px 48px',
                fontSize: '18px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                boxShadow: '0 8px 24px rgba(244, 206, 123, 0.4)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(244, 206, 123, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(244, 206, 123, 0.4)';
              }}
            >
              Get API Key
              <Key size={20} />
            </button>
            <button
              onClick={() => navigate('/partners')}
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '50px',
                padding: '20px 48px',
                fontSize: '18px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.border = '2px solid rgba(255, 255, 255, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.border = '2px solid rgba(255, 255, 255, 0.3)';
              }}
            >
              View All Partnerships
            </button>
          </div>

          <div style={{
            marginTop: '48px',
            display: 'flex',
            gap: '32px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Phone size={18} color="#F4CE7B" />
              <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '16px' }}>
                +91 99999 00000
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Mail size={18} color="#F4CE7B" />
              <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '16px' }}>
                developers@gewohealth.com
              </span>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// Import missing icons
import { Calendar, Activity, TrendingUp } from 'lucide-react';

export default ApiPartners;